import { useCallback, useState } from 'react';
import {
  Content,
  Inline,
  InlineItem,
  Placeholder,
  Secondary,
  Stack,
  Text,
  UtilityButton,
} from '@kinesis/bungle';
import type { CatalogLibrary, InstalledEfLibrary } from '@/types';
import { LibraryRow } from './library-row';
import { AddLibraryModal } from './add-library-modal';

// A scenario's emission factor library list — a port of suit's
// `ScenarioEmissionFactorLibraries`, shifted to village's `InstalledEfLibrary`
// shape. Each editable capability is opt-in: the parent decides whether the
// user can reorder (`onReorder`), change pin state (`onPinChange`), attach
// (`onAttach` + `catalog`) or remove (`onRemove`) libraries independently. A
// viewer passes none of these and the list renders read-only.
type ScenarioEmissionFactorLibrariesProps = {
  libraries: InstalledEfLibrary[];
  // The inventory's attach catalog. Required for the add-library modal but
  // optional here — when absent the "Add library" button is hidden.
  catalog?: CatalogLibrary[];
  onReorder?: (orderedLibraryIds: number[]) => void;
  onPinChange?: (libraryId: number, pinned: boolean) => void;
  onAttach?: (libraryId: number) => void;
  onRemove?: (libraryId: number) => void;
};

const reorderArray = <T,>(items: T[], from: number, to: number): T[] => {
  if (
    from === to ||
    from < 0 ||
    to < 0 ||
    from >= items.length ||
    to >= items.length
  ) {
    return items;
  }
  const next = items.slice();
  const [moved] = next.splice(from, 1);
  next.splice(to, 0, moved);
  return next;
};

const ScenarioEmissionFactorLibraries = ({
  libraries,
  catalog,
  onReorder,
  onPinChange,
  onAttach,
  onRemove,
}: ScenarioEmissionFactorLibrariesProps) => {
  const pinnedCount = libraries.filter((l) => l.version != null).length;

  const [draggingIndex, setDraggingIndex] = useState<number | null>(null);
  const [isAddOpen, setAddOpen] = useState(false);

  const handleDragStart = useCallback((index: number) => {
    setDraggingIndex(index);
  }, []);

  const handleDrop = useCallback(
    (targetIndex: number) => {
      if (draggingIndex === null || !onReorder) {
        setDraggingIndex(null);
        return;
      }
      const next = reorderArray(libraries, draggingIndex, targetIndex);
      setDraggingIndex(null);
      if (next !== libraries) {
        onReorder(next.map((l) => l.efLibrary));
      }
    },
    [draggingIndex, libraries, onReorder],
  );

  const handleOpenAdd = useCallback(() => setAddOpen(true), []);
  const handleCloseAdd = useCallback(() => setAddOpen(false), []);
  const handleSelectAdd = useCallback(
    (libraryId: number) => {
      setAddOpen(false);
      onAttach?.(libraryId);
    },
    [onAttach],
  );

  const canAttach = typeof onAttach === 'function' && Array.isArray(catalog);
  const draggable = typeof onReorder === 'function';
  const attachedIds = libraries.map((l) => l.efLibrary);

  // Summary + Add-button toolbar, shown whether or not the list is empty — the
  // user needs somewhere to click even when starting from scratch.
  const toolbar = (
    <Content alignX='right'>
      <Inline>
        <InlineItem sizing='fill-container'>
          <Secondary>
            <Text>
              {libraries.length}{' '}
              {libraries.length === 1 ? 'library' : 'libraries'} attached
              {pinnedCount > 0
                ? ` · ${pinnedCount} pinned`
                : libraries.length > 0
                  ? ' · all following latest'
                  : ''}
            </Text>
          </Secondary>
        </InlineItem>
        {canAttach && (
          <InlineItem>
            <UtilityButton
              icon='plus'
              magnitude='small'
              onClick={handleOpenAdd}
            >
              <Text>Add library</Text>
            </UtilityButton>
          </InlineItem>
        )}
      </Inline>
    </Content>
  );

  const emptyBody = (
    <Content
      alignX='center'
      textAlign='center'
      padding='medium'
      paddingMode='equal'
    >
      <Placeholder>
        No emission factor libraries are attached to this scenario.
      </Placeholder>
    </Content>
  );

  return (
    <Stack space='medium'>
      {libraries.length === 0
        ? emptyBody
        : libraries.map((library, index) => (
            // A scenario attaches a given library at most once, so the library
            // id is a stable row key.
            <LibraryRow
              key={library.efLibrary}
              library={library}
              index={index}
              draggable={draggable}
              onDragStart={handleDragStart}
              onDrop={handleDrop}
              onPinChange={onPinChange}
              onRemove={onRemove}
            />
          ))}
      {toolbar}
      {isAddOpen && canAttach && (
        <AddLibraryModal
          catalog={catalog!}
          attachedLibraryIds={attachedIds}
          onClose={handleCloseAdd}
          onSelect={handleSelectAdd}
        />
      )}
    </Stack>
  );
};

export { ScenarioEmissionFactorLibraries, reorderArray };
export type { ScenarioEmissionFactorLibrariesProps };
